import React, { ReactChild, FunctionComponent } from 'react';
import { Pagination, PaginationProps } from '@doveit/lego';
import { highlightToHTMLString } from '@doveit/hammer';
import { PropertySearchResult } from '../../../services/dtos/property';
import PropertyCard from '../property-card/PropertyCard';
import * as styles from './PropertiesList.style';
import { Status } from '../../../domain/status';
import { buildListingURLObject } from '../../utils/links';

export interface PropertiesListProps extends React.AriaAttributes {
  properties: PropertySearchResult[],
  pagination?: {
    totalPages: number,
    totalElements: number,
    pageNumber: number,
    pageSize: number,
    onPageChange: PaginationProps['navigateTo'],
    buildPagePath?: PaginationProps['buildPagePath'],
  },
  render?: {
    inPosition?: (index: number) => ReactChild,
  },
  interactive?: boolean,
  onItemClick: (property: PropertySearchResult) => void,
}

const SOLD = 'Venduto';
const OFFER = 'Trattativa';

const PropertiesList: FunctionComponent<PropertiesListProps> = ({
  properties,
  onItemClick,
  render = {},
  interactive = true,
  pagination,
  ...rest
}) => {
  const getPropertyBanner = (status: Status) => {
    if (status === Status.VENDUTO) {
      return SOLD;
    }

    if (status === Status.PROPOSTA) {
      return OFFER;
    }

    return undefined;
  };

  return (
    <>
      <ul
        {...rest}
        css={styles.list}
      >
        {properties.map((property, index) => (
          <div key={property.id}>
            <li
              css={styles.item}
              data-ref="search-result"
            >
              <PropertyCard
                title={property.title}
                price={property.price}
                urlObject={interactive ? buildListingURLObject(property) : undefined}
                onClick={interactive ? () => onItemClick(property) : undefined}
                suburb={property.suburb}
                banner={getPropertyBanner(property.status)}
                features={property.highlights.map(highlightToHTMLString)}
                image={property.coverImageSet.medium}
                description={property.abstract}
                ariaLabel={property.title}
                promoted={property.promoted}
              />
            </li>
            {render.inPosition && render.inPosition(index)}
          </div>
        ))}
      </ul>

      {(pagination && pagination.totalPages > 1) && (
        <div css={styles.pagination}>
          <Pagination
            current={pagination.pageNumber + 1}
            totalItems={pagination.totalElements}
            pageSize={pagination.pageSize}
            navigateTo={pagination.onPageChange}
            buildPagePath={pagination.buildPagePath}
          />
        </div>
      )}
    </>
  );
};

export default PropertiesList;
